import { useEffect, useRef, useState, type ChangeEvent } from 'react'
import { FileUp, X } from 'lucide-react'
import { Button } from '@/components/ds2'
import { RevisionCard } from './RevisionCard'
import { CHAT_UPLOAD_ACCEPT, createLoadFromRatecon, revisionResultText, uploadErrorText, type OpenedRevision, type RevisionChoice } from './uploadFlow'

interface AttachPanelProps {
    onUploaded: (message: string) => void
    onClose: () => void
}

/**
 * The composer's attach (D15): a rate con, which the board's own upload reads to create a load (D16). A POD or a BOL is
 * uploaded from its load's row or card, where the load and the type are already known. The file goes to the board's
 * upload, never to the agent (D17). A con for a load the board already has is shown as that load's revision, with the
 * board's choices, until the user picks one; the chat is then told what came of it.
 */
export const AttachPanel = ({ onUploaded, onClose }: AttachPanelProps) => {
    const input = useRef<HTMLInputElement>(null)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [revision, setRevision] = useState<OpenedRevision | null>(null)

    // One line per revision, however the panel goes away: a choice, its Close, or the composer's paperclip unmounting it.
    const said = useRef(false)
    const choosing = useRef(false)
    const mounted = useRef(true)
    const shown = useRef<OpenedRevision | null>(null)
    const latestOnUploaded = useRef(onUploaded)
    const [submitting, setSubmitting] = useState(false)

    useEffect(() => {
        latestOnUploaded.current = onUploaded
    }, [onUploaded])

    const say = (revisionShown: OpenedRevision, choice: RevisionChoice): boolean => {
        if (said.current) return false
        said.current = true
        latestOnUploaded.current(revisionResultText(revisionShown, choice))
        return true
    }

    // Unmounted without a choice (the paperclip closes the panel without Close): the revision stays pending on the
    // board, and the chat still learns of the con. A choice still running says its own line when it ends.
    useEffect(() => {
        mounted.current = true
        return () => {
            mounted.current = false
            if (shown.current && !choosing.current) say(shown.current, { kind: 'left' })
        }
    }, [])

    const showRevision = (opened: OpenedRevision) => {
        shown.current = opened
        setRevision(opened)
    }

    const onPicked = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        event.target.value = ''
        if (!file) return
        setBusy(true)
        setError(null)
        try {
            const upload = await createLoadFromRatecon(file)
            if (upload.kind === 'revision') {
                showRevision(upload.revision)
                return
            }
            onUploaded(upload.message)
            onClose()
        } catch (err) {
            setError(uploadErrorText(err))
        } finally {
            setBusy(false)
        }
    }

    const onSubmittingChange = (running: boolean) => {
        choosing.current = running
        if (mounted.current) setSubmitting(running)
        // Unmounted while a choice ran and it failed: nothing was decided, so the revision still waits on the board.
        else if (!running && shown.current) say(shown.current, { kind: 'left' })
    }

    const finish = (revisionShown: OpenedRevision, choice: RevisionChoice) => {
        if (say(revisionShown, choice)) onClose()
    }

    // Closed before a choice: the revision stays pending on the board, and the chat still learns of the con.
    const close = () => (revision ? finish(revision, { kind: 'left' }) : onClose())

    return (
        <div role="group" aria-label="Attach a file" className="flex flex-col gap-2 rounded-xl bg-surface p-3 shadow-surface">
            <div className="flex items-center gap-2">
                {!revision && (
                    <>
                        <Button size="sm" loading={busy} prefix={<FileUp className="h-3.5 w-3.5" />} onClick={() => input.current?.click()}>
                            Upload a rate con to create a load
                        </Button>
                        <input
                            ref={input}
                            type="file"
                            accept={CHAT_UPLOAD_ACCEPT}
                            className="hidden"
                            aria-label="Rate con file"
                            onChange={(e) => void onPicked(e)}
                        />
                    </>
                )}
                <Button size="sm" variant="ghost" iconOnly aria-label="Close" className="ml-auto" disabled={submitting} onClick={close}>
                    <X className="h-4 w-4" />
                </Button>
            </div>
            {revision ? (
                <RevisionCard revision={revision} onResolved={(choice) => finish(revision, choice)} onSubmittingChange={onSubmittingChange} />
            ) : busy ? (
                <span role="status" className="text-xs text-foreground-muted">
                    Reading the rate con. This can take up to two minutes.
                </span>
            ) : (
                <span className="text-xs text-foreground-muted">PDF, JPEG or PNG. To add a POD or BOL, use the upload on that load.</span>
            )}
            {error && (
                <p role="alert" className="text-xs text-danger-soft-foreground">
                    {error}
                </p>
            )}
        </div>
    )
}
